import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, RouterModule } from '@angular/router';
import { ClubSwitcherComponent } from '@3ddv/software-division-components/backoffice/club-switcher';
import { ServicesDropdownComponent } from '@3ddv/software-division-components/backoffice/services-dropdown';
import {
  FooterComponent,
  HeaderComponent,
  ShellComponent,
  SidebarComponent,
} from '@3ddv/software-division-components/backoffice/shell';
import { UserMenuComponent } from '@3ddv/software-division-components/backoffice/user-menu';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { filter, map } from 'rxjs/operators';
import appPackage from '../../../package.json';
import { environment } from 'src/environments/environment';
import { AuthService } from '../core/services/auth.service';
import { hasRouteAccess } from '../core/auth/route-permissions';
import {
  clubSwitchUrl,
  toClubSwitcherItems,
} from '../core/layout/club-switcher-data';
import {
  sdcEnvironmentFor,
  toProductSwitcherItems,
} from '../core/layout/product-switcher-data';
import { CURRENT_SERVICE_KEY } from '../core/models/auth.model';
import { AuthStore } from '../core/stores/auth.store';
import { Client } from './events/models/client.model';
import { PrivateSellingService } from './group-links/services/private-selling.service';
import {
  ADMIN_TOOLS_ITEMS,
  NAVIGATION_ICONS,
  NAVIGATION_ITEMS,
} from './layout/navigation';
import { ConfigurationService } from './services/configuration.service';

const PRODUCT_NAME = 'Group Sales';
const SUPPORT_EMAIL = 'support@3ddigitalvenue.com';

@Component({
  selector: 'app-private',
  templateUrl: './private.component.html',
  styleUrls: ['./private.component.css'],
  imports: [
    RouterModule,
    NgIcon,
    ShellComponent,
    SidebarComponent,
    HeaderComponent,
    FooterComponent,
    UserMenuComponent,
    ServicesDropdownComponent,
    ClubSwitcherComponent,
  ],
  viewProviders: [provideIcons(NAVIGATION_ICONS)],
})
export class PrivateComponent {
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthStore);
  private readonly privateSellingService = inject(PrivateSellingService);
  private readonly configurationService = inject(ConfigurationService);

  protected readonly appName = this.configurationService.configuration.appName;
  protected readonly logo = this.configurationService.configuration.logo;
  protected readonly productName = PRODUCT_NAME;
  protected readonly supportEmail = SUPPORT_EMAIL;
  protected readonly version = appPackage.version;

  protected readonly activeMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored',
  };

  protected readonly expanded = signal(false);

  private readonly client = signal<Client | undefined>(undefined);

  protected readonly services = computed(() =>
    toProductSwitcherItems(this.authStore.services())
  );

  protected readonly sdcEnv = computed(() =>
    sdcEnvironmentFor(environment.serviceTier)
  );

  protected readonly club = computed(() => this.authStore.club() ?? '');

  protected readonly currentServiceKey = CURRENT_SERVICE_KEY;

  protected readonly isAdmin = this.authStore.isAdmin;

  protected readonly clubs = toClubSwitcherItems();

  protected readonly navigationItems = computed(() => {
    const context = { client: this.client() };
    const roles = this.authStore.tags();

    return NAVIGATION_ITEMS.filter(
      item =>
        hasRouteAccess(item.path, roles) && (item.isVisible?.(context) ?? true)
    );
  });

  protected readonly adminToolsItems = computed(() => {
    const roles = this.authStore.tags();

    return ADMIN_TOOLS_ITEMS.filter(
      item => item.path !== 'users' && hasRouteAccess(item.path, roles)
    );
  });

  protected readonly username = computed(
    () =>
      this.authStore.userFullName()?.trim() || this.authStore.userEmail() || ''
  );

  constructor() {
    this.privateSellingService
      .getClientWithWhitelist(null)
      .pipe(takeUntilDestroyed())
      .subscribe({ next: res => this.client.set(res.data) });
  }

  protected switchClub(target: { slug: string }): void {
    location.assign(clubSwitchUrl(location, this.club(), target.slug));
  }

  protected toggleSidebar(): void {
    this.expanded.update(isExpanded => !isExpanded);
  }

  protected logout(): void {
    this.authService.logout();
  }
}
